"use client";
// beui.dev/components/motion/button
// Restyled for Bavimail (see DESIGN.md §5 Buttons): signal-orange primary with
// ink text, hairline/dashed secondaries, pill or sharp corners, 0.97 press.

import { AnimatePresence, type HTMLMotionProps, motion, useReducedMotion } from "motion/react";
import {
  forwardRef,
  type PointerEvent,
  type ReactNode,
  useCallback,
  useRef,
  useState,
} from "react";
import { EASE_OUT, SPRING_PRESS } from "@/lib/ease";
import { useHoverCapable } from "@/lib/hooks/use-hover-capable";
import { cn } from "@/lib/utils";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "ghost"
  | "outline"
  | "danger"
  | "danger-outline";
/** `icon` is 32px; `icon-sm` (24px) is for dense rows, chips and inline tool icons. */
export type ButtonSize = "sm" | "md" | "lg" | "icon" | "icon-sm";
/** Pills for primary/secondary actions, sharp (6px) for in-table or compact controls. */
export type ButtonShape = "pill" | "sharp";

export interface ButtonProps extends Omit<HTMLMotionProps<"button">, "children"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  shape?: ButtonShape;
  pressScale?: number;
  /** Hover lift on hover-capable devices. Pass 1 to disable (e.g. joined split buttons). */
  hoverScale?: number;
  /** Spawn a Material-style ripple from the press point. Off by default. */
  ripple?: boolean;
  children?: ReactNode;
}

export interface ButtonLinkProps extends Omit<HTMLMotionProps<"a">, "children"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  shape?: ButtonShape;
  pressScale?: number;
  hoverScale?: number;
  children?: ReactNode;
}

type Ripple = { id: number; x: number; y: number; size: number };

const BASE_CLASS = cn(
  "inline-flex shrink-0 items-center justify-center whitespace-nowrap select-none",
  "transition-colors duration-[var(--motion-duration-fast)]",
  "outline-none focus-visible:ring-2 focus-visible:ring-hb-ring focus-visible:ring-offset-2 focus-visible:ring-offset-hb-page",
  "disabled:pointer-events-none disabled:opacity-40",
);

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  primary: "bg-hb-accent font-hb font-bold text-hb-accent-fg hover:bg-hb-accent-hover",
  secondary:
    "border border-dashed border-hb-card font-mono-hb text-hb-muted hover:border-hb-border-hover hover:text-hb-heading",
  ghost: "font-mono-hb text-hb-muted hover:bg-hb-raised/70 hover:text-hb-heading",
  outline:
    "border border-hb-card bg-transparent font-hb font-bold text-hb-text hover:border-hb-border-hover hover:text-hb-heading",
  danger: "bg-hb-danger font-hb font-bold text-hb-accent-fg hover:bg-hb-danger-hover",
  "danger-outline":
    "border border-dashed border-hb-card font-mono-hb text-hb-danger hover:border-hb-danger/40 hover:bg-hb-danger/10",
};

const MONO_VARIANTS: ReadonlySet<ButtonVariant> = new Set(["secondary", "ghost", "danger-outline"]);

const SIZE_CLASS: Record<ButtonSize, string> = {
  sm: "h-7 gap-1.5 px-3",
  md: "h-9 gap-2 px-4",
  lg: "h-[46px] gap-2.5 px-7",
  icon: "h-8 w-8",
  "icon-sm": "h-6 w-6",
};

// Mono labels stay small: they are texture and wayfinding, not headlines.
const TEXT_CLASS: Record<"sans" | "mono", Record<ButtonSize, string>> = {
  sans: { sm: "text-xs", md: "text-sm", lg: "text-base", icon: "text-sm", "icon-sm": "text-xs" },
  mono: {
    sm: "text-[11px]",
    md: "text-[11px]",
    lg: "text-xs",
    icon: "text-[11px]",
    "icon-sm": "text-[11px]",
  },
};

const SHAPE_CLASS: Record<ButtonShape, string> = {
  pill: "rounded-full",
  sharp: "rounded-sm",
};

/** Class list for the branded button, for elements that can't be a `Button`. */
export function buttonClassName({
  variant = "primary",
  size = "md",
  shape = "pill",
  className,
}: {
  variant?: ButtonVariant;
  size?: ButtonSize;
  shape?: ButtonShape;
  className?: string;
} = {}) {
  return cn(
    BASE_CLASS,
    VARIANT_CLASS[variant],
    SIZE_CLASS[size],
    TEXT_CLASS[MONO_VARIANTS.has(variant) ? "mono" : "sans"][size],
    SHAPE_CLASS[shape],
    className,
  );
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "primary",
    size = "md",
    shape = "pill",
    pressScale = 0.97,
    hoverScale = 1.02,
    ripple = false,
    className,
    children,
    onPointerDown,
    ...rest
  },
  ref,
) {
  const reduce = useReducedMotion();
  const canHover = useHoverCapable();
  const [ripples, setRipples] = useState<Ripple[]>([]);
  const nextId = useRef(0);

  const handlePointerDown = useCallback(
    (event: PointerEvent<HTMLButtonElement>) => {
      if (ripple && !reduce) {
        const rect = event.currentTarget.getBoundingClientRect();
        const size = Math.max(rect.width, rect.height) * 2;
        const id = nextId.current++;
        setRipples((prev) => [
          ...prev,
          {
            id,
            x: event.clientX - rect.left,
            y: event.clientY - rect.top,
            size,
          },
        ]);
      }
      onPointerDown?.(event);
    },
    [ripple, reduce, onPointerDown],
  );

  return (
    <motion.button
      ref={ref}
      type="button"
      whileTap={reduce ? undefined : { scale: pressScale }}
      whileHover={reduce || !canHover ? undefined : { scale: hoverScale }}
      transition={SPRING_PRESS}
      onPointerDown={handlePointerDown}
      className={buttonClassName({
        variant,
        size,
        shape,
        className: cn(ripple && "relative overflow-hidden", className),
      })}
      {...rest}
    >
      {ripple && !reduce ? (
        <span className="pointer-events-none absolute inset-0 overflow-hidden rounded-[inherit]">
          <AnimatePresence>
            {ripples.map((r) => (
              <motion.span
                key={r.id}
                className="absolute rounded-full bg-current"
                style={{
                  left: r.x,
                  top: r.y,
                  width: r.size,
                  height: r.size,
                  x: "-50%",
                  y: "-50%",
                }}
                initial={{ scale: 0.05, opacity: 0.3 }}
                animate={{ scale: 1, opacity: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1.6, ease: EASE_OUT }}
                onAnimationComplete={() => setRipples((prev) => prev.filter((x) => x.id !== r.id))}
              />
            ))}
          </AnimatePresence>
        </span>
      ) : null}
      {children}
    </motion.button>
  );
});

export const ButtonLink = forwardRef<HTMLAnchorElement, ButtonLinkProps>(function ButtonLink(
  {
    variant = "primary",
    size = "md",
    shape = "pill",
    pressScale = 0.97,
    hoverScale = 1.02,
    className,
    children,
    ...rest
  },
  ref,
) {
  const reduce = useReducedMotion();
  const canHover = useHoverCapable();

  return (
    <motion.a
      ref={ref}
      whileTap={reduce ? undefined : { scale: pressScale }}
      whileHover={reduce || !canHover ? undefined : { scale: hoverScale }}
      transition={SPRING_PRESS}
      className={buttonClassName({ variant, size, shape, className })}
      {...rest}
    >
      {children}
    </motion.a>
  );
});
